import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import deepMerge from 'deepmerge';
import DeleteIcon from '@material-ui/icons/Delete';

import {
    loadUserRoles,
    updateRolesFilter,
    loadUserRolesHistory,
    updateRolesHistoryFilter,
    deleteUserRole,
} from 'Actions/userRoles';
import { pageTitleDecorator, lightboxDecorator } from 'Decorators';
import { QueryFilterType, DataTableMetadataType } from 'Types';
import { DATA_TABLE_COLUMN_TYPE_DATE, DATA_TABLE_COLUMN_TYPE_LINK, DATA_TABLE_COLUMN_TYPE_ACTIONS } from 'Constants';

import DataTable from 'Components/DataTable';
import UserAddRole from './UserAddRole';
import FormGroup from 'Components/FormGroup/FormGroup';

@connect(
    store => ({
        filter: store.userRolesList.filter,
        metadata: store.userRolesList.metadata,
        userRolesList: store.userRolesList.data,
        historyFilter: store.userRolesHistoryList.filter,
        historyMetadata: store.userRolesHistoryList.metadata,
        userRolesHistoryList: store.userRolesHistoryList.data,
    }),
    dispatch => ({
        loadUserRoles: filter => dispatch(loadUserRoles(filter)),
        updateRolesFilter: filter => dispatch(updateRolesFilter(filter)),
        loadUserRolesHistory: filter => dispatch(loadUserRolesHistory(filter)),
        updateRolesHistoryFilter: filter => dispatch(updateRolesHistoryFilter(filter)),
        deleteUserRole: id => dispatch(deleteUserRole(id)),
    })
)
@pageTitleDecorator('users.roles')
@lightboxDecorator({
    title: 'title.warning',
    text: 'alert.users.roles.delete',
})
export default class UserRolesList extends PureComponent {
    /**
     * PropTypes
     */
    static propTypes = {
        match: PropTypes.object.isRequired,
        filter: QueryFilterType,
        metadata: DataTableMetadataType,
        userRolesList: PropTypes.any.isRequired,
        historyFilter: QueryFilterType,
        historyMetadata: DataTableMetadataType,
        userRolesHistoryList: PropTypes.any.isRequired,
        openLightbox: PropTypes.func.isRequired,
        loadUserRoles: PropTypes.func.isRequired,
        updateRolesFilter: PropTypes.func.isRequired,
        loadUserRolesHistory: PropTypes.func.isRequired,
        updateRolesHistoryFilter: PropTypes.func.isRequired,
        deleteUserRole: PropTypes.func.isRequired,
    };

    /**
     * Get default filter
     *
     * @param filter
     * @returns {*}
     */
    getFilter = filter => {
        const {
            match: {
                params: { id },
            },
        } = this.props;

        return deepMerge(filter, { where: { user_id: id } });
    };

    /**
     * Get roles list table columns
     *
     * @returns {*[]}
     */
    getColumns = () => {
        return [
            {
                key: 'role.name',
                col: { width: '25%' },
                title: 'data_table.role.name',
                sortable: false,
            },
            {
                key: 'journal.title',
                col: { width: '45%' },
                title: 'data_table.journal_id',
                sortable: false,
                type: DATA_TABLE_COLUMN_TYPE_LINK,
                link: {
                    to: '/journals/:id',
                    placeholders: {
                        id: item => item.journal_id,
                    },
                },
            },
            {
                key: 'created_at',
                col: { width: '15%' },
                title: 'data_table.from',
                sortable: false,
                type: DATA_TABLE_COLUMN_TYPE_DATE,
            },
            {
                key: 'id',
                col: { width: '15%' },
                title: 'data_table.empty',
                type: DATA_TABLE_COLUMN_TYPE_ACTIONS,
                actions: [
                    {
                        name: 'Delete',
                        icon: <DeleteIcon />,
                        onClick: item => this.deleteUserRoleHandler(item.id),
                    },
                ],
            },
        ];
    };

    /**
     * Get roles history table columns
     *
     * @returns {*[]}
     */
    getHistoryColumns = () => {
        return [
            {
                key: 'role.name',
                col: { width: '25%' },
                title: 'data_table.role.name',
                sortable: false,
            },
            {
                key: 'journal.title',
                col: { width: '45%' },
                title: 'data_table.journal_id',
                sortable: false,
                type: DATA_TABLE_COLUMN_TYPE_LINK,
                link: {
                    to: '/journals/:id',
                    placeholders: {
                        id: item => item.journal_id,
                    },
                },
            },
            {
                key: 'from',
                col: { width: '15%' },
                title: 'data_table.from',
                sortable: false,
                type: DATA_TABLE_COLUMN_TYPE_DATE,
            },
            {
                key: 'to',
                col: { width: '15%' },
                title: 'data_table.to',
                type: DATA_TABLE_COLUMN_TYPE_DATE,
            },
        ];
    };

    /**
     * Delete role handler
     *
     * @param id
     */
    deleteUserRoleHandler = id => {
        const { deleteUserRole, openLightbox } = this.props;

        openLightbox(() => deleteUserRole(id));
    };

    /**
     * Render roles list table
     *
     * @returns {*}
     */
    renderRolesList = () => {
        const { filter, metadata, userRolesList, loadUserRoles, updateRolesFilter } = this.props;

        return (
            <DataTable
                columns={this.getColumns()}
                metadata={metadata}
                data={userRolesList}
                filter={this.getFilter(filter)}
                load={loadUserRoles}
                updateFilter={updateRolesFilter}
                icon={false}
                customClass='active-roles-table-wrapper'
                customCardClass='active-roles-table-card'
            />
        );
    };

    /**
     * Render role history table
     *
     * @returns {*}
     */
    renderRolesHistoryList() {
        const {
            historyFilter,
            historyMetadata,
            userRolesHistoryList,
            loadUserRolesHistory,
            updateRolesHistoryFilter,
        } = this.props;

        return (
            <DataTable
                columns={this.getHistoryColumns()}
                metadata={historyMetadata}
                data={userRolesHistoryList}
                filter={this.getFilter(historyFilter)}
                load={loadUserRolesHistory}
                updateFilter={updateRolesHistoryFilter}
                icon={false}
                customClass='history-roles-table-wrapper'
            />
        );
    }

    /**
     * Render add role
     *
     * @returns {*}
     */
    renderAddRole = () => {
        const { match } = this.props;

        return <UserAddRole match={match} />;
    };

    /**
     * Render
     *
     * @returns {*}
     */
    render() {
        return (
            <div className='user-roles-list-wrapper'>
                <FormGroup title="form_groups.active_roles">
                    {this.renderRolesList()}
                </FormGroup>
                {this.renderAddRole()}
                <FormGroup title="form_groups.roles_history" customClassWrapper='user-roles-grouper'>
                    {this.renderRolesHistoryList()}
                </FormGroup>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/UserEdit/containers/UserRolesList.jsx